import classNames from 'classnames';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import * as portals from 'react-reverse-portal';
import { debounce } from 'throttle-debounce';

import { globalStyles, useUIStyle } from 'client-web-sdk';

import { ReactComponent as Background } from 'src/images/login_register_background.svg';
import { useGlobalBannerContext } from 'src/util/GlobalBannerContext';

import styles from './FullscreenFrame.module.css';

type Props = JSX.IntrinsicElements['div'] &
  PropsWithChildren<{
    open?: boolean;
    onOpenEnd?: () => void;
  }>;

export default function FullscreenFrame({ children, open, onOpenEnd, className, ...props }: Props) {
  const fallbackOpenEndTimeoutIdRef = useRef<number>();

  // Call onOpenEnd once the "open" transition has finished
  const debouncedOnTransitionEnd = debounce(10, () => {
    if (open) {
      window.clearTimeout(fallbackOpenEndTimeoutIdRef.current);
      onOpenEnd?.();
    }
  });

  // There is pathological situation where open is toggled so fast that the transition is not
  // rendered. In this case onTransitionEnd is never called. This fallback mechanism prevents that.
  useEffect(() => {
    if (open) {
      if (onOpenEnd) {
        fallbackOpenEndTimeoutIdRef.current = window.setTimeout(onOpenEnd, 1000);
      }
    } else {
      window.clearTimeout(fallbackOpenEndTimeoutIdRef.current);
    }
  });

  const uiStyle = useUIStyle();

  const { bannerPortalNode } = useGlobalBannerContext();

  return (
    <div
      className={classNames(
        styles.FullscreenFrame,
        {
          [globalStyles.defaultStyle]: uiStyle === 'default',
          [globalStyles.highlightStyle]: uiStyle === 'highlight',
          [styles.open]: open,
        },
        className,
      )}
      {...props}
    >
      {bannerPortalNode && (
        <div className={styles.Banner}>
          <portals.OutPortal node={bannerPortalNode} />
        </div>
      )}
      <Background
        className={styles.Background}
        role="presentation"
        onTransitionEnd={debouncedOnTransitionEnd}
      />
      {children}
    </div>
  );
}
